import React, { useEffect, useState } from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button as MuiButton,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Grid,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import LockIcon from '@material-ui/icons/Lock';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { tenantActions } from '../../../redux/tenant.actions';
import Text from '../../../../../components/text';

const useStyles = makeStyles({
  asterisk: {
    color: '#EF4444',
  },
});

const getInitials = (string) => {
  if (!string) return '';
  return string
    .trim()
    .split(/\s+/)
    .reduce((acc, word) => acc + word.slice(0, 1), '')
    .toUpperCase();
};

const LandlordCard = ({
  landlord,
  flowId,
  handleMakeLead,
  handleEdit,
  handleDelete,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState({});
  const [showBankAccounts, setShowBankAccounts] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Bank Account Form State
  const [formData, setFormData] = useState({
    account_name: '',
    account_id: '',
    sort_code: '',
    swift_code: '',
    iban: '',
  });
  const [errors, setErrors] = useState({});

  const landlordId = landlord?.open_flow_landlord_id || landlord?.tenant_id || landlord?.id;
  const currentFlowId = flowId || landlord?.openflow_id || landlord?.flow_id;

  const fetchBankAccounts = () => {
    if (landlordId) {
      dispatch(
        tenantActions.getLandlordBankAccounts(landlordId, (accounts) => {
          if (Array.isArray(accounts)) {
            setBankAccounts(accounts);
          } else {
            setBankAccounts([]);
          }
        })
      );
    }
  };

  useEffect(() => {
    fetchBankAccounts();
  }, [landlordId]);

  const getFullName = () => {
    const first = landlord?.first_name || '';
    const last = landlord?.last_name || '';
    return `${first} ${last}`.trim().toUpperCase() || 'LANDLORD';
  };

  const displayName = `${landlord?.title ? `${landlord.title}. ` : ''}${getFullName()}`;

  const handleOpenAddAccount = () => {
    setSelectedAccount({});
    setFormData({
      account_name: '',
      account_id: '',
      sort_code: '',
      swift_code: '',
      iban: '',
    });
    setErrors({});
    setShowBankAccounts(true);
  };

  const handleEditAccountClick = (account) => {
    setSelectedAccount(account);
    setFormData({
      account_name: account?.account_name || '',
      account_id: account?.account_id ? String(account.account_id) : '',
      sort_code: account?.sort_code ? String(account.sort_code) : '',
      swift_code: account?.swift_code || '',
      iban: account?.iban || '',
    });
    setErrors({});
    setShowBankAccounts(true);
  };

  const handleDeleteAccountClick = (account) => {
    setSelectedAccount(account);
    setShowDeleteModal(true);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.account_name?.trim()) {
      newErrors.account_name = 'Account Name is required';
    }
    const cleanAccountId = String(formData.account_id || '').trim();
    if (!cleanAccountId) {
      newErrors.account_id = 'Account Number is required';
    } else if (!/^\d{8}$/.test(cleanAccountId)) {
      newErrors.account_id = 'Must be exactly 8 digits';
    }
    const cleanSortCode = String(formData.sort_code || '').trim();
    if (!cleanSortCode) {
      newErrors.sort_code = 'Sort Code is required';
    } else if (!/^\d{6}$/.test(cleanSortCode)) {
      newErrors.sort_code = 'Must be exactly 6 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveBankAccount = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    const payload = {
      account_name: formData.account_name.trim(),
      account_id: formData.account_id.trim(),
      sort_code: formData.sort_code ? formData.sort_code.trim() : "",
      swift_code: formData.swift_code ? formData.swift_code.trim() : "",
      iban: formData.iban ? formData.iban.trim() : "",
      flow_id: currentFlowId,
      landlord_id: landlordId,
    };

    const isEdit = selectedAccount && Object.keys(selectedAccount).length > 0;
    const accountId = selectedAccount?.landlord_bank_account_id || selectedAccount?.id || selectedAccount?.account_id;

    if (isEdit && accountId) {
      dispatch(
        tenantActions.editLandlordBankAccount(accountId, payload, (success) => {
          setLoading(false);
          if (success) {
            setShowBankAccounts(false);
            setSelectedAccount({});
            fetchBankAccounts();
          }
        })
      );
    } else {
      dispatch(
        tenantActions.addLandlordBankAccount(landlordId, payload, (success) => {
          setLoading(false);
          if (success) {
            setShowBankAccounts(false);
            setSelectedAccount({});
            fetchBankAccounts();
          }
        })
      );
    }
  };

  const handleConfirmDeleteAccount = () => {
    const accountId = selectedAccount?.landlord_bank_account_id || selectedAccount?.id || selectedAccount?.account_id;
    if (!accountId) return;

    setLoading(true);
    dispatch(
      tenantActions.deleteLandlordBankAccount(accountId, (success) => {
        setLoading(false);
        if (success) {
          setShowDeleteModal(false);
          setSelectedAccount({});
          fetchBankAccounts();
        }
      })
    );
  };

  const initials = getInitials(getFullName());
  const isLead = Boolean(landlord?.is_lead);

  return (
    <div style={styles.cardContainer}>
      {/* Top Header */}
      <div style={styles.headerRow}>
        <div style={styles.avatarAndTitle}>
          {/* Avatar Circle */}
          <div
            style={{
              ...styles.avatar,
              backgroundColor: isLead ? 'rgba(144, 196, 24, 0.16)' : 'rgba(102, 108, 255, 0.14)',
              color: isLead ? '#3B7A11' : '#5B50FF',
            }}
          >
            {initials || 'L'}
          </div>

          {/* Name and verified badge */}
          <div style={styles.titleColumn}>
            <div style={styles.nameText}>
              {displayName}
            </div>
            {landlord?.is_email_verified && (
              <div style={styles.verifiedBadge}>
                <CheckCircleIcon style={{ fontSize: 13, color: '#16A34A', marginRight: 3 }} />
                <span style={{ fontSize: 11, color: '#16A34A', fontWeight: '600' }}>Verified</span>
              </div>
            )}
          </div>
        </div>

        {/* Right Header Actions */}
        <div style={styles.headerActions}>
          {!isLead && handleMakeLead && (
            <Tooltip title="Make Lead">
              <button
                type="button"
                onClick={() => handleMakeLead(landlord)}
                style={styles.makeLeadButton}
              >
                MAKE LEAD
              </button>
            </Tooltip>
          )}

          {handleEdit && (
            <Tooltip title="Edit Landlord">
              <IconButton
                size="small"
                onClick={() => handleEdit(landlord)}
                style={{ color: '#0288D1', padding: 5 }}
              >
                <EditIcon style={{ fontSize: 19 }} />
              </IconButton>
            </Tooltip>
          )}

          {handleDelete && (
            <Tooltip title="Delete Landlord">
              <IconButton
                size="small"
                onClick={() => handleDelete(landlord)}
                style={{ color: '#EF4444', padding: 5 }}
              >
                <DeleteIcon style={{ fontSize: 19 }} />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Details List */}
      <div style={styles.bodyDetails}>
        <div style={styles.detailRow}>
          Email : {landlord?.email || '-'}
        </div>
        <div style={styles.detailRow}>
          Postcode : {landlord?.post_code || landlord?.postcode || '-'}
        </div>
        <div style={styles.detailRow}>
          Contact Number : {landlord?.number || landlord?.contact_number || '-'}
        </div>
      </div>

      {/* Action: Add Bank Account */}
      <div style={styles.actionRow}>
        <button
          type="button"
          onClick={handleOpenAddAccount}
          style={styles.addBankAccountBtn}
        >
          ADD BANK ACCOUNT
        </button>
      </div>

      {/* Bank Accounts List */}
      {bankAccounts && bankAccounts.length > 0 && (
        <div style={styles.bankAccountsContainer}>
          {bankAccounts.map((item, index) => (
            <div key={item.landlord_bank_account_id || item.id || index} style={styles.bankAccountCard}>
              <div style={styles.bankAccountHeader}>
                <div style={styles.accountText}>
                  <b>Account name</b> - {item.account_name}
                </div>
                <div style={styles.accountActions}>
                  <Tooltip title="Edit details">
                    <IconButton
                      size="small"
                      onClick={() => handleEditAccountClick(item)}
                      style={{ color: '#0288D1', padding: 3 }}
                    >
                      <EditIcon style={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete account">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteAccountClick(item)}
                      style={{ color: '#EF4444', padding: 3 }}
                    >
                      <DeleteIcon style={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
              <div style={styles.accountText}>
                <b>Account Id</b> - {item.account_id}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog: Add / Edit Bank Account */}
      <Dialog
        open={showBankAccounts}
        onClose={() => {
          if (!loading) {
            setShowBankAccounts(false);
            setSelectedAccount({});
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ fontWeight: '600', color: '#1E293B', paddingBottom: 8 }}>
          {selectedAccount && Object.keys(selectedAccount).length ? 'Edit' : 'Add'} bank account for{' '}
          {getFullName()}
        </DialogTitle>
        <DialogContent>
          <form noValidate autoComplete="off" onSubmit={handleSaveBankAccount}>
            <Grid container spacing={3} style={{ marginTop: 4 }}>
              {/* Account Name */}
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Account Name"
                  placeholder="Account name"
                  value={formData.account_name}
                  onChange={(e) => {
                    setFormData({ ...formData, account_name: e.target.value });
                    if (errors.account_name) {
                      setErrors((prev) => ({ ...prev, account_name: undefined }));
                    }
                  }}
                  error={Boolean(errors.account_name)}
                  helperText={errors.account_name}
                  InputLabelProps={{
                    classes: { asterisk: classes.asterisk },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccountBalanceIcon style={{ fontSize: 20, color: '#64748B' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Account Number */}
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Account Number"
                  placeholder="Account Number"
                  value={formData.account_id}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/[^0-9]/g, '').slice(0, 8);
                    setFormData({ ...formData, account_id: cleaned });
                    if (errors.account_id) {
                      setErrors((prev) => ({ ...prev, account_id: undefined }));
                    }
                  }}
                  error={Boolean(errors.account_id)}
                  helperText={errors.account_id}
                  InputLabelProps={{
                    classes: { asterisk: classes.asterisk },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CreditCardIcon style={{ fontSize: 20, color: '#64748B' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Sort Code */}
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Sort Code"
                  placeholder="Sort Code"
                  value={formData.sort_code}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                    setFormData({ ...formData, sort_code: cleaned });
                    if (errors.sort_code) {
                      setErrors((prev) => ({ ...prev, sort_code: undefined }));
                    }
                  }}
                  error={Boolean(errors.sort_code)}
                  helperText={errors.sort_code}
                  InputLabelProps={{
                    classes: { asterisk: classes.asterisk },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon style={{ fontSize: 20, color: '#64748B' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* SWIFT Code */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Swift Code (Optional)"
                  placeholder="Swift Code"
                  value={formData.swift_code}
                  onChange={(e) => setFormData({ ...formData, swift_code: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon style={{ fontSize: 20, color: '#64748B' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* IBAN */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="IBAN (Optional)"
                  placeholder="IBAN"
                  value={formData.iban}
                  onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CreditCardIcon style={{ fontSize: 20, color: '#64748B' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px', justifyContent: 'center' }}>
          <MuiButton
            onClick={() => {
              setShowBankAccounts(false);
              setSelectedAccount({});
            }}
            variant="outlined"
            color="default"
            disabled={loading}
            style={{ textTransform: 'none', marginRight: 12, minWidth: 100 }}
          >
            Cancel
          </MuiButton>
          <MuiButton
            onClick={handleSaveBankAccount}
            variant="contained"
            color="primary"
            disabled={loading}
            style={{
              textTransform: 'none',
              backgroundColor: '#8c57ff',
              color: '#FFFFFF',
              minWidth: 100,
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Dialog: Delete Account Confirmation */}
      <Dialog
        open={showDeleteModal}
        onClose={() => {
          if (!loading) setShowDeleteModal(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle style={{ fontWeight: '600' }}>Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ color: '#475569' }}>
            Are you sure, You want to delete this account.
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <MuiButton
            onClick={() => setShowDeleteModal(false)}
            variant="outlined"
            color="default"
            disabled={loading}
            style={{ textTransform: 'none' }}
          >
            No
          </MuiButton>
          <MuiButton
            onClick={handleConfirmDeleteAccount}
            variant="contained"
            disabled={loading}
            style={{
              backgroundColor: '#EF4444',
              color: '#FFFFFF',
              textTransform: 'none',
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Yes'}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

const styles = {
  cardContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    border: '1px solid #E2E8F0',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    width: '100%',
    fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  headerRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
  },
  avatarAndTitle: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
    minWidth: 0,
  },
  avatar: {
    width: '42px',
    height: '42px',
    minWidth: '42px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: '14px',
    letterSpacing: '0.5px',
  },
  titleColumn: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  nameText: {
    fontWeight: '700',
    fontSize: '15px',
    color: '#1E293B',
    lineHeight: '20px',
    wordBreak: 'break-word',
  },
  verifiedBadge: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '2px',
  },
  headerActions: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '4px',
    marginLeft: '8px',
  },
  makeLeadButton: {
    backgroundColor: '#8c57ff',
    color: '#FFFFFF',
    fontSize: '11px',
    fontWeight: '600',
    border: 'none',
    borderRadius: '20px',
    padding: '4px 11px',
    cursor: 'pointer',
    marginRight: '4px',
    outline: 'none',
    letterSpacing: '0.5px',
    boxShadow: '0 2px 4px rgba(140, 87, 255, 0.3)',
  },
  bodyDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    marginBottom: '14px',
  },
  detailRow: {
    fontSize: '13px',
    color: '#475569',
    lineHeight: '20px',
  },
  actionRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '10px',
  },
  addBankAccountBtn: {
    background: 'none',
    border: 'none',
    padding: 0,
    color: '#0288D1',
    fontWeight: '600',
    fontSize: '13px',
    cursor: 'pointer',
    letterSpacing: '0.3px',
    outline: 'none',
  },
  bankAccountsContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '6px',
  },
  bankAccountCard: {
    backgroundColor: '#F8FAFC',
    border: '1px solid #E2E8F0',
    borderRadius: '8px',
    padding: '12px 14px',
    marginBottom: '10px',
  },
  bankAccountHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px',
  },
  accountText: {
    fontSize: '13px',
    color: '#1E293B',
    lineHeight: '18px',
  },
  accountActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
  },
};

export default LandlordCard;
